import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { sendEmailVerification, onIdTokenChanged } from 'firebase/auth';
import AuthLayout from '../../components/auth/AuthLayout';
import { useNotification } from '../../contexts/NotificationContext';
import { auth } from '../../firebase/config';

function EmailVerification() {
    const { currentUser, checkEmailVerification } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const { showNotification } = useNotification();

    // 手動で認証状態を確認する関数（AuthContextの関数を使用）
    const checkVerificationStatus = async () => {
        setVerifying(true);
        try {
            // AuthContextの関数を使用して認証状態を確認
            const isVerified = await checkEmailVerification();

            if (isVerified) {
                showNotification('メールアドレスの認証が完了しました', 'success');
                navigate('/profile-setup');
            } else {
                showNotification('まだメール認証が完了していません。メール内のリンクをクリックしてください。', 'warning');
            }
        } catch (error) {
            console.error('認証確認エラー:', error);
            showNotification('認証状態の確認中にエラーが発生しました', 'error');
        } finally {
            setVerifying(false);
        }
    };

    useEffect(() => {
        if (!currentUser) {
            navigate('/login');
            return;
        }

        // メール認証が完了している場合
        if (currentUser.emailVerified) {
            showNotification('メールアドレスの認証が完了しました', 'success');
            navigate('/profile-setup');
            return;
        }

        // Firebaseのトークン変更を監視（認証状態の変更を検知）
        const unsubscribe = onIdTokenChanged(auth, async (user) => {
            if (user && user.emailVerified) {
                // 認証状態が検知されたら、AuthContextの関数で状態を更新
                const isVerified = await checkEmailVerification();
                if (isVerified) {
                    showNotification('メールアドレスの認証が完了しました', 'success');
                    navigate('/profile-setup');
                }
            }
        });

        // URLパラメータで認証済みフラグがある場合
        const urlParams = new URLSearchParams(window.location.search);
        const verified = urlParams.get('verified');

        if (verified === 'true' && currentUser) {
            // パラメータ付きでリダイレクトされた場合、認証確認を即時実行
            checkVerificationStatus();
        }

        return () => unsubscribe();
    }, [currentUser, navigate, checkEmailVerification]);

    const handleResendEmail = async () => {
        setLoading(true);
        try {
            await sendEmailVerification(currentUser, {
                url: `${window.location.origin}/email-verification?verified=true`
            });
            showNotification('認証メールを再送信しました', 'success');
        } catch (error) {
            showNotification('認証メールの送信に失敗しました', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthLayout>
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h5" component="h1" gutterBottom>
                    メール認証
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                    {currentUser?.email} 宛に認証メールを送信しました。
                    メール内のリンクをクリックして、メールアドレスの認証を完了してください。
                </Typography>
                <Typography variant="body2" sx={{ mb: 3 }}>
                    メールが届かない場合は、迷惑メールフォルダをご確認いただくか、
                    以下のボタンから再送信してください。
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                    <Button
                        variant="contained"
                        onClick={handleResendEmail}
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={24} /> : '認証メールを再送信'}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={checkVerificationStatus}
                        disabled={verifying}
                    >
                        {verifying ? <CircularProgress size={24} /> : '認証状態を確認'}
                    </Button>
                </Box>
            </Box>
        </AuthLayout>
    );
}

export default EmailVerification;